import styled, { x } from '@xstyled/styled-components';
import React, { useEffect, useState } from 'react';
import { t } from 'ttag';
import { select } from '~/lib/select';
import { Button } from '~/ui/buttons/Button';
import { Flex } from '~/ui/flex/Flex';
import { LlamaModal } from '~/ui/llama-modal/LlamaModal';
import { Logo } from '~/ui/logo/Logo';
import { TrainingState, useTrainingStore } from '../state';
import { UpdateProfileMutationVariables } from '~/data';
import { updateProfileData } from '~/training/state/settings/updateProfileData';
import { useTrainingParams } from '~/training/useTrainingParams';
import { useRouteMatch } from 'react-router';
import { Tooltip } from '~/ui/tooltip/Tooltip';
import ReactSelect from 'react-select';
import { useSequenceStore } from '~/training/sequence/SequenceStore';

const SimpleTooltip = styled.small`
  background-color: #fcf8e9;
  color: #000;
  border-radius: 20px;
  padding: 0px 5px 0px 5px;
  cursor: help;
  position: absolute;
  margin-left: 5px;
`;

const PromptWrapper = styled.div`
  .flex-block-for-model {
    width: 100%;
    max-height: 95vh;
    overflow: auto;
  }
  .modalStyleForDarkTheme {
    height: fit-content;
  }
  .click_continue {
    max-width: 340px;
    text-align: center;
  }
`;

const WrapperForTooltip = styled.span`
  .tooltipForRole {
    margin-top: -45px;
    margin-left: 162px;
    width: 260px;
    z-index: 100;
  }
`;

const selector = (s: TrainingState) =>
  select(
    s,
    'firstName',
    'companyName',
    'logoUrl',
    'title',
    'promptOptions',
    'promptProfile'
  );

export const PromptProfile = () => {
  const { learnerTrainingId } = useTrainingParams();
  const { companyName, logoUrl, promptOptions, promptProfile } =
    useTrainingStore(selector);
  const { url } = useRouteMatch();
  const { sendRxdInteractions } = useSequenceStore();
  const [role, setRole] = useState('');
  const [industry, setIndustry] = useState('');
  const [region, setRegion] = useState('');
  const [subordinateRegions, setSubordinateRegions] = useState<string[]>([]);
  const [confirmation, setConfirmation] = useState(false);
  const [disableSaveBtn, setDisableSaveBtn] = useState(true);
  const [showTooltip, setShowTooltip] = useState(false);

  interface RxdInterface {
    RecordMultipleChoiceInteraction(
      id: string,
      response: string,
      correct: boolean,
      correctResponse: string,
      description: string
    ): void;
  }

  interface WindowWithRxd extends Window {
    RXD?: RxdInterface;
  }

  // Get RXD object for CMI integration
  let RXD: RxdInterface | undefined;
  try {
    if (window.document.referrer) {
      const preUrl = window?.document?.referrer;
      const domain = preUrl?.split('/')[2];

      if (domain !== 'localhost:8000' && domain !== 'www.easyllama.com') {
        RXD = (window.parent as WindowWithRxd).RXD;
      }
    } else {
      RXD = (window.parent as WindowWithRxd).RXD;
    }
  } catch (error) {
    console.error('RXD in PromptProfile:', error);
  }

  const getValueForPromptOption = (id: string | number, optionType: string) => {
    const optionMap: { [key: string]: [number, string][] } = {
      industries: promptOptions?.industries || [],
      regions: promptOptions?.regions || [],
      subordinate_regions: promptOptions?.subordinate_regions || []
    };

    const options = optionMap[optionType];
    if (!options) {
      throw new Error(`Invalid option type: ${optionType}`);
    }

    const option = options.find(el => el[0] == id);
    return option ? option[1] : '';
  };

  useEffect(() => {
    if (!promptProfile && window && window.location) {
      window.location.href = `${url}`
        .replace('//', '/')
        .replace('form', 'onboarding');
    }
  }, [promptProfile]);

  useEffect(() => {
    if (
      !promptOptions ||
      !promptOptions.roles ||
      promptOptions.roles.length == 0
    ) {
      setDisableSaveBtn(false);
    }
  }, []);

  const showConfirmation = () => {
    setConfirmation(true);
  };

  const SendCmiIntegration = (
    role: string,
    industry: string,
    region: string,
    subordinateRegions: string[] | undefined
  ) => {
    if (!sendRxdInteractions || !RXD) {
      return;
    }

    try {
      // Send role selection
      if (role) {
        RXD.RecordMultipleChoiceInteraction(
          'profile-role',
          role,
          true,
          role,
          'Role: '
        );
      }

      // Send industry selection
      if (industry) {
        RXD.RecordMultipleChoiceInteraction(
          'profile-industry',
          industry,
          true,
          industry,
          `Industry: `
        );
      }

      // Send region selection
      if (region) {
        RXD.RecordMultipleChoiceInteraction(
          'profile-region',
          region,
          true,
          region,
          `Location: `
        );
      }

      // Send subordinate regions (multiple choice for supervisors)
      if (
        role === 'supervisor' &&
        subordinateRegions &&
        subordinateRegions.length > 0
      ) {
        subordinateRegions.forEach((subRegion, index) => {
          RXD.RecordMultipleChoiceInteraction(
            `profile-subordinate-region-${index}`,
            subRegion,
            true,
            subRegion,
            `Direct Reports' Regions: `
          );
        });
      }
    } catch (error) {
      console.error('Error in SendCmiIntegration:', error);
    }
  };

  const sendData = async () => {
    // Send CMI integration data before saving
    SendCmiIntegration(
      role,
      getValueForPromptOption(industry, 'industries'),
      getValueForPromptOption(region, 'regions'),
      subordinateRegions.map(region =>
        getValueForPromptOption(region, 'subordinate_regions')
      )
    );

    const dataForSending = {
      learnerTrainingUuid: learnerTrainingId,
      role: role,
      industryId: industry ? Number(industry) : undefined,
      regionId: region ? Number(region) : undefined,
      subordinateRegionIds:
        role == 'supervisor'
          ? subordinateRegions && subordinateRegions.length > 0
            ? subordinateRegions.map(Number)
            : undefined
          : undefined
    } as UpdateProfileMutationVariables;

    const res__ = await updateProfileData(dataForSending);

    if (res__ && window && window.location) {
      const numberToReplace = res__.id;
      const regex = /\/t\/(\d+)\/form/;

      window.location.href = `${url}`
        .replace('//', '/')
        .replace(regex, `/t/${numberToReplace}/form`)
        .replace('form', 'onboarding');
    }
  };

  return (
    <PromptWrapper>
      <LlamaModal horizontal="center" vertical="center">
        <Flex
          w="md"
          minHeight="md"
          flexDirection="column"
          p="xl"
          alignItems="center"
          justifyContent="space-between"
          className="flex-block-for-model"
        >
          <x.div className="logoDark">
            <Logo src={logoUrl} height={36} />
            {!logoUrl && <p style={{ paddingBottom: '20px' }}>{companyName}</p>}
          </x.div>
          <x.h2 fontFamily="llama">{t`Welcome!`}</x.h2>
          {confirmation ? (
            <div className="click_continue">
              <p>{t`By clicking on "Continue", I certify that the information below is correct and that I will have to retake the course if it is incorrect.`}</p>
              {role && (
                <div>
                  {t`Role`}: {role}
                </div>
              )}
              {industry && (
                <div>
                  {t`Industry`}:{' '}
                  {getValueForPromptOption(industry, 'industries')}
                </div>
              )}
              {region && (
                <div>
                  {t`Region`}: {getValueForPromptOption(region, 'regions')}
                </div>
              )}
              {role === 'supervisor' &&
                subordinateRegions &&
                subordinateRegions.length > 0 && (
                  <div>
                    {t`Subordinate Regions`}:{' '}
                    {subordinateRegions
                      .map(region =>
                        getValueForPromptOption(region, 'subordinate_regions')
                      )
                      .join(', ')}
                  </div>
                )}
            </div>
          ) : (
            <div>
              <p>{t`Please answer a few questions about yourself:`}</p>
              <div style={{ marginTop: '12px', maxWidth: '340px' }}>
                <x.h3>
                  {t`Select Your Role`}{' '}
                  {showTooltip && (
                    <WrapperForTooltip>
                      <Tooltip
                        placement={'left'}
                        className={'tooltipForRole'}
                      >{t`You are a supervisor if you manage people and have direct reports.`}</Tooltip>
                    </WrapperForTooltip>
                  )}
                  <SimpleTooltip
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                  >
                    ?
                  </SimpleTooltip>
                </x.h3>
                <ReactSelect
                  options={(promptOptions?.roles || []).map((l: string) => ({
                    value: l,
                    label: l
                  }))}
                  onChange={selectedOption => {
                    setRole(selectedOption ? selectedOption.value : '');
                    setDisableSaveBtn(false);
                  }}
                  value={role ? { value: role, label: role } : null}
                  placeholder={t`Select`}
                  styles={{
                    control: base => ({
                      ...base,
                      backgroundColor: '#FFF',
                      color: '#000'
                    }),
                    menu: base => ({
                      ...base,
                      backgroundColor: '#FFF',
                      color: '#000'
                    }),
                    option: base => ({
                      ...base,
                      backgroundColor: '#FFF',
                      color: '#000'
                    })
                  }}
                />
              </div>
              {promptOptions?.industries && (
                <div style={{ marginTop: '12px' }}>
                  <x.h3>{t`Industry`}</x.h3>
                  <ReactSelect
                    options={Object.values(promptOptions?.industries).map(
                      (l: [number, string]) => ({
                        value: l[0] as unknown as string,
                        label: l[1]
                      })
                    )}
                    onChange={selectedOption => {
                      setIndustry(selectedOption ? selectedOption.value : '');
                    }}
                    value={
                      industry
                        ? {
                            value: industry,
                            label: getValueForPromptOption(
                              industry,
                              'industries'
                            )
                          }
                        : null
                    }
                    placeholder={t`Select an option`}
                    styles={{
                      control: base => ({
                        ...base,
                        backgroundColor: '#FFF',
                        color: '#000'
                      }),
                      menu: base => ({
                        ...base,
                        backgroundColor: '#FFF',
                        color: '#000'
                      }),
                      option: base => ({
                        ...base,
                        backgroundColor: '#FFF',
                        color: '#000'
                      }),
                      menuList: base => ({
                        ...base,
                        maxHeight: '200px',
                        overflowY: 'auto'
                      })
                    }}
                  />
                </div>
              )}
              {promptOptions?.regions && (
                <div style={{ marginTop: '12px' }}>
                  <x.h3>{t`Select Your Location`}</x.h3>
                  <ReactSelect
                    options={Object.values(promptOptions?.regions).map(
                      (l: [number, string]) => ({
                        value: l[0] as unknown as string,
                        label: l[1]
                      })
                    )}
                    menuPlacement="top"
                    onChange={selectedOption => {
                      setRegion(selectedOption ? selectedOption.value : '');
                    }}
                    value={
                      region
                        ? {
                            value: region,
                            label: getValueForPromptOption(region, 'regions')
                          }
                        : null
                    }
                    placeholder={t`Select an option`}
                    styles={{
                      control: base => ({
                        ...base,
                        backgroundColor: '#FFF',
                        color: '#000'
                      }),
                      menu: base => ({
                        ...base,
                        backgroundColor: '#FFF',
                        color: '#000'
                      }),
                      option: base => ({
                        ...base,
                        backgroundColor: '#FFF',
                        color: '#000'
                      }),
                      menuList: base => ({
                        ...base,
                        maxHeight: '120px',
                        overflowY: 'auto'
                      })
                    }}
                  />
                </div>
              )}
              {role === 'supervisor' &&
                promptOptions?.subordinate_regions &&
                promptOptions?.subordinate_regions.length > 0 && (
                  <div style={{ marginTop: '12px' }}>
                    <x.h3>{t`Select Your Direct Reports’ Regions`}</x.h3>
                    <ReactSelect
                      options={promptOptions?.subordinate_regions.map(
                        (region: [number, string]) => ({
                          value: region[0] as unknown as string,
                          label: region[1]
                        })
                      )}
                      isMulti
                      menuPlacement="top"
                      onChange={selectedOptions => {
                        setSubordinateRegions(
                          selectedOptions
                            ? selectedOptions.map(
                                option => option.value as string
                              )
                            : []
                        );
                      }}
                      placeholder={t`Select an option`}
                      value={subordinateRegions.map(region => ({
                        value: region,
                        label: getValueForPromptOption(
                          region,
                          'subordinate_regions'
                        )
                      }))}
                      styles={{
                        control: base => ({
                          ...base,
                          backgroundColor: '#FFF',
                          color: '#000',
                          maxWidth: '340px'
                        }),
                        menu: base => ({
                          ...base,
                          backgroundColor: '#FFF',
                          color: '#000',
                          zIndex: 9999,
                          marginBottom: '8px'
                        }),
                        menuPortal: base => ({
                          ...base,
                          zIndex: 9999
                        }),
                        option: base => ({
                          ...base,
                          backgroundColor: '#FFF',
                          color: '#000'
                        }),
                        menuList: base => ({
                          ...base,
                          maxHeight: '120px',
                          overflowY: 'auto'
                        }),
                        multiValue: base => ({
                          ...base,
                          backgroundColor: 'var(--color-accent-dk)',
                          color: '#FFF',
                          borderRadius: '10px'
                        }),
                        multiValueLabel: base => ({
                          ...base,
                          color: '#FFF'
                        })
                      }}
                    />
                  </div>
                )}
            </div>
          )}
          {confirmation ? (
            <div>
              <Button
                style={{ marginRight: '20px' }}
                mt={'20px'}
                onClick={() => setConfirmation(false)}
              >{t`Back`}</Button>
              <Button mt={'20px'} onClick={sendData}>{t`Continue`}</Button>
            </div>
          ) : (
            <Button
              mt={'20px'}
              onClick={showConfirmation}
              disabled={disableSaveBtn}
            >{t`Save`}</Button>
          )}
        </Flex>
      </LlamaModal>
    </PromptWrapper>
  );
};
